import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { KeyRound, ShieldOff, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ACTIVE_OTPS_QUERY,
  LOAD_OTP_PACK_MUTATION,
  REVOKE_OTP_MUTATION,
} from './otp-admin.gql';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { OTP_ACTION_FROM_GQL, OTP_ACTION_TO_GQL } from '@/shared/graphql/enums';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface Otp {
  id: string;
  action_type: string;
  status: 'active' | 'burned' | 'revoked' | 'expired';
  created_at: string;
  expires_at: string | null;
  created_by: string;
}

type OtpAction = 'discount' | 'sale_cancel' | 'cash_out' | 'cash_in' | 'admin_override';

interface OtpsResponse {
  activeOtps: Otp[];
}

const ACTIONS: readonly OtpAction[] = [
  'discount',
  'sale_cancel',
  'cash_out',
  'cash_in',
  'admin_override',
];

export function OtpAdminPage(): JSX.Element {
  const { t } = useTranslation();
  const [filter_action, setFilterAction] = useState<OtpAction | 'all'>('all');
  const [show_load, setShowLoad] = useState(false);
  const [error_text, setErrorText] = useState<string | null>(null);

  const { data, refetch } = useQuery<OtpsResponse>(ACTIVE_OTPS_QUERY, {
    variables: { action: filter_action === 'all' ? null : OTP_ACTION_TO_GQL[filter_action], limit: 200 },
    fetchPolicy: 'cache-and-network',
  });
  const items = (data?.activeOtps ?? []).map((item) => ({
    ...item,
    action_type: OTP_ACTION_FROM_GQL[item.action_type as keyof typeof OTP_ACTION_FROM_GQL] ?? item.action_type,
  }));
  const [revokeOtp] = useMutation(REVOKE_OTP_MUTATION);

  const onRevoke = async (id: string): Promise<void> => {
    setErrorText(null);
    if (!window.confirm(t('otp_admin.revoke_confirm'))) return;
    try {
      await revokeOtp({ variables: { otp_id: id } });
      await refetch();
    } catch (err) {
      if (err instanceof ApolloError) setErrorText(err.graphQLErrors[0]?.message ?? err.message);
      else if (err instanceof Error) setErrorText(err.message);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('otp_admin.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('otp_admin.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <select
            className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filter_action}
            onChange={(e) => setFilterAction(e.target.value as OtpAction | 'all')}
          >
            <option value="all">{t('otp_admin.filter_all')}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {t(`otp_admin.action_${a}`)}
              </option>
            ))}
          </select>
          <Button type="button" onClick={() => setShowLoad(true)}>
            <Upload className="h-4 w-4" aria-hidden />
            <span>{t('otp_admin.load_pack')}</span>
          </Button>
        </div>
      </header>

      {error_text ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error_text}
        </div>
      ) : null}

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t('otp_admin.empty')}
          </CardContent>
        </Card>
      ) : (
        <ul className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {items.map((o) => (
            <li key={o.id}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <KeyRound className="h-4 w-4" aria-hidden />
                      <Badge variant="muted">{t(`otp_admin.action_${o.action_type}`)}</Badge>
                    </span>
                    <Badge variant="success">{t('otp_admin.status_active')}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    {t('otp_admin.created_at')}: {fmtDate(o.created_at)}
                  </div>
                  <div>
                    {t('otp_admin.expires_at')}:{' '}
                    {o.expires_at ? fmtDate(o.expires_at) : t('otp_admin.never_expires')}
                  </div>
                  <div className="font-mono text-[10px]">id={o.id.slice(0, 8)}</div>
                </CardContent>
                <CardFooter className="justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void onRevoke(o.id);
                    }}
                  >
                    <ShieldOff className="h-4 w-4 text-destructive" aria-hidden />
                    <span>{t('otp_admin.revoke')}</span>
                  </Button>
                </CardFooter>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {show_load ? (
        <LoadPackDialog
          on_close={() => setShowLoad(false)}
          on_done={async () => {
            setShowLoad(false);
            await refetch();
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}

function LoadPackDialog({
  on_close,
  on_done,
  on_error,
}: {
  on_close: () => void;
  on_done: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [action, setAction] = useState<OtpAction>('discount');
  const [plaintexts_raw, setPlaintextsRaw] = useState('');
  const [expires_in_days, setExpiresInDays] = useState('');
  const [load, { loading, data }] = useMutation<{
    loadOtpPack: { inserted: number; duplicates: number };
  }>(LOAD_OTP_PACK_MUTATION);

  const lines = plaintexts_raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    if (lines.length === 0) {
      on_error(t('otp_admin.error_empty'));
      return;
    }
    const expires_at = expires_in_days
      ? new Date(Date.now() + Number(expires_in_days) * 86_400_000).toISOString()
      : null;
    try {
      await load({
        variables: {
          input: {
            action: OTP_ACTION_TO_GQL[action],
            plaintexts: lines,
            expires_at,
          },
        },
      });
      setPlaintextsRaw('');
      await on_done();
    } catch (err) {
      if (err instanceof ApolloError) on_error(err.graphQLErrors[0]?.message ?? err.message);
      else if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>{t('otp_admin.load_dialog_title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('otp_admin.load_dialog_hint')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('otp_admin.action')}</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={action}
                onChange={(e) => setAction(e.target.value as OtpAction)}
                disabled={loading}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(`otp_admin.action_${a}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t('otp_admin.expires_in_days')}</Label>
              <Input
                type="number"
                min={0}
                value={expires_in_days}
                onChange={(e) => setExpiresInDays(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t('otp_admin.plaintexts')}</Label>
            <textarea
              rows={10}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
              value={plaintexts_raw}
              onChange={(e) => setPlaintextsRaw(e.target.value)}
              disabled={loading}
              placeholder={t('otp_admin.plaintexts_placeholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('otp_admin.plaintexts_hint', { count: lines.length })}
            </p>
          </div>
          {data?.loadOtpPack ? (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs">
              {t('otp_admin.load_result', {
                inserted: data.loadOtpPack.inserted,
                duplicates: data.loadOtpPack.duplicates,
              })}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={lines.length === 0 || loading}
          >
            {loading ? t('common.loading') : t('otp_admin.load_pack')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
